import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

/**
 * Catches render-time errors anywhere below it and shows a recoverable fallback
 * instead of an unmounted blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surfaced to the console / any attached monitoring.
    console.error('ErrorBoundary caught an error:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center bg-background">
          <h1 className="text-3xl font-black text-black dark:text-white tracking-[-0.02em]">
            Something went wrong
          </h1>
          <p className="mt-4 max-w-md text-neutral-500 dark:text-neutral-400 font-light leading-relaxed">
            An unexpected error occurred. Please reload the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-8 inline-flex items-center px-6 py-3 rounded-[5px] bg-[#0f637b] dark:bg-[#04daf6] text-white dark:text-black font-semibold transition-all hover:opacity-80"
          >
            Reload
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
